import { ObjectWith } from '../../../cross-team-shared/types/misc';
import { Answer, AnswersByName } from './answer';
import { PointCloudShapeType } from './point-cloud-answer';

export interface ExternalShapeLayer<P extends PointType, S extends ShapeType> {
  frame_count: number;
  sources?: LocationSource[];
  shapes: Array<ShapeExternal<P, S>>;
}

export interface ShapeLayerExternalObjectless<
  P extends PointType,
  S extends ShapeType,
> {
  frame_count: number;
  shape_groups: Array<ShapeGroupExternal<P, S>>;
  sources?: LocationSource[];
}

export type VectorTaggingLayerExternal = ShapeLayerExternalObjectless<
  Point2D,
  VectorTaggingShapeType
>;

export const vectorTaggingShapeTypes = [
  'arrow',
  'cuboid',
  'hair',
  'keypoint_shape',
  'line',
  'model_input_points',
  'point',
  'polygon',
  'rectangle',
  'slider_rectangle',
] as const;

export type VectorTaggingShapeType = (typeof vectorTaggingShapeTypes)[number];

export type VectorTaggingShapeGroupExternal = ShapeGroupExternal<
  Point2D,
  VectorTaggingShapeType
>;
export type VectorTaggingShapeExternal = ShapeExternal<
  Point2D,
  VectorTaggingShapeType
>;

export interface ShapeGroupExternal<P extends PointType, T extends ShapeType> {
  group_type: string | null;
  shapes: Array<ShapeExternal<P, T>>;
}

export interface ShapeBase<T extends ShapeType> {
  type: T;
  index: number;
  tags: ObjectWith<Answer>;
}

export interface ShapeExternal<P extends PointType, T extends ShapeType>
  extends ShapeBase<T> {
  key_locations: Array<LocationExternal<P>>;
}

export interface FrameSpecificTag {
  frame_number: number;
  value: Answer;
}

export type LocationExternal<P extends PointType> =
  | ShapeLocationExternal<P>
  | TagLocation
  | (ShapeLocationExternal<P> & TagLocation);

export interface ShapeLocationBase<P extends PointType> {
  frame_number: number;
  points: P[];
  visibility: VisibilityState;
  model_input_points?: P[];
}

export interface ShapeLocation<P extends PointType>
  extends ShapeLocationBase<P> {
  source?: LocationSource;
}

export interface ShapeLocationExternal<P extends PointType>
  extends ShapeLocationBase<P> {
  source?: number;
}

export interface LocationSource {
  name: string;
  model_id: string;
}

export interface TagLocation {
  frame_number: number;
  tags: AnswersByName;
}

export type Point2D = [number, number]; // [x, y]
export type Point3D = [number, number, number]; // [x, y, z]
export type PointType = Point2D | Point3D;
export type VisibilityState = 0 | 0.5 | 1;
export type ShapeType = VectorTaggingShapeType | PointCloudShapeType;
